import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import { useAuthorization } from 'framework/authorization';
import {
  READ_BACKGROUND_CHECK_LIST,
  UPDATE_BACKGROUND_CHECK,
} from 'domain/people/employee/PersonalLegal/backgroundcheck/shared/employeeBackgroundCheckService';
import { ShowSection } from 'framework/ui/layout';
import { InputLoader } from 'framework/ui/mui-builder';
import { useListContext } from 'framework/list';
import BackgroundCheckCreateForm from 'domain/people/employee/PersonalLegal/backgroundcheck/createupdate/BackgroundCheckCreateForm';
import BackgroundCheckItem from './BackgroundCheckItem';
import { BackgroundCheckContentProps } from './types';
import { useIsMyProfile } from 'domain/shared/employee';

const BackgroundCheckContent: React.FC<BackgroundCheckContentProps> = ({ employeeId }) => {
  const { data, loading, reloadData } = useListContext();
  const isMyProfile = useIsMyProfile();
  const { isGranted } = useAuthorization();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const items = data?.items || [];
  const hasAccess = isGranted(READ_BACKGROUND_CHECK_LIST);
  const canAddBackgroundCheck = isGranted([UPDATE_BACKGROUND_CHECK]) && !isMyProfile;
  const hasItems = items.length > 0;

  if (!hasAccess) {
    return null;
  }

  const handleUpdate = () => {
    setEditingId(null);
    reloadData?.();
  };

  const handleCreateSave = () => {
    setIsCreating(false);
    reloadData?.();
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setIsCreating(true);
  };

  const renderAddButton = () => {
    if (!canAddBackgroundCheck || isCreating) {
      return null;
    }

    return (
      <Button onClick={handleAddClick} startIcon={<Add />} variant="outlined" size="small" sx={{ marginTop: 1.5 }}>
        Add background check
      </Button>
    );
  };

  const renderLoadingState = () => (
    <Box display="flex" justifyContent="center">
      <InputLoader />
    </Box>
  );

  const renderEmptyState = () => (
    <Box display="flex" flexDirection="column" alignItems="center" p={1}>
      <Typography variant="body1" color="textSecondary" mb={3} textAlign="center">
        No background checks have been performed for this worker yet.
      </Typography>
      {renderAddButton()}
    </Box>
  );

  const renderCreateForm = () => (
    <BackgroundCheckCreateForm employeeId={employeeId} onSave={handleCreateSave} onCancel={handleCreateCancel} />
  );

  const renderItemsList = () => (
    <>
      {items.map((backgroundCheck: any) => (
        <BackgroundCheckItem
          key={getBackgroundCheckKey(backgroundCheck)}
          backgroundCheck={backgroundCheck}
          onUpdate={handleUpdate}
          disabled={!!editingId && editingId !== backgroundCheck.id}
        />
      ))}
      {isCreating && employeeId && renderCreateForm()}
      {renderAddButton()}
    </>
  );

  const getBackgroundCheckKey = (backgroundCheck: any) => {
    return `${backgroundCheck.id}-${backgroundCheck.attachmentsIds?.length || 0}-${JSON.stringify(backgroundCheck.attachmentsIds)}`;
  };

  const showSectionProps = {
    sx: {
      position: 'relative',
      '.show-title-container': {
        marginBottom: '10px',
      },
    } as const,
    title: (
      <HowToRegOutlinedIcon
        sx={{
          color: 'text.primary',
        }}
      />
    ),
    disableDefaultPadding: true,
  };

  const getContent = () => {
    if (loading) {
      return renderLoadingState();
    }

    if (!hasItems && !isCreating) {
      return renderEmptyState();
    }

    if (!hasItems && isCreating) {
      return renderCreateForm();
    }

    return renderItemsList();
  };

  return (
    <ShowSection {...showSectionProps}>
      <Box>{getContent()}</Box>
    </ShowSection>
  );
};

export default BackgroundCheckContent;
